import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import produce from 'immer';
import clsx from 'clsx';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, setIsEditingForm, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { ApiError, DeviceClass } from '@moderepo/mode-internal-apis';
import { GatewayDefinition, AppAPI } from '@moderepo/mode-apis';
import { SelectInputOption, DeviceBasicInfo, DeviceBasicInfoProps, capitalizeAll, capitalizeFirst, BaseDeviceInfoUpdatableInputs, getErrorMessage,
    BaseInfoCompProps } from '@moderepo/mode-app-components';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById, updateProjectDevice }
    from '@moderepo/mode-data-state-ritz';
import { fetchHomeDevice, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { useSelectProjectDeviceClasses } from '../../utils/dataStateHooks';



interface InputErrors {
    deviceClass: string | null;
}


export interface DeviceBasicInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly deviceId: number | undefined;       // undefined if we are in create device mode
    readonly gatewaysOnly?: boolean;             // If create device mode, this flag indicate only allow create device classes that are gateways
    readonly userId: number | undefined;         // Only available if the user is viewing Device info from a user's home
    readonly homeId: number | undefined;
    readonly newDeviceSettings?: {               // Only available if this container is used for creating new user
        readonly onNewDeviceAdded: ()=> void;
        readonly onNewDeviceCanceled: ()=> void;
    }

    // This container used DeviceBasicInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;
}


export const DeviceBasicInfoContainer = (props: DeviceBasicInfoContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const selectedDevice = props.deviceId ? selectProjectDeviceById(dataState, props.deviceId) : undefined;
    const deviceClasses = useSelectProjectDeviceClasses(props.projectId);
    const gatewayCatalog = useGetProjectGatewayCatalog(project);

    // find the gatewayDefinition for the selected device from the gateway catalog. If we find a gatewayDefinition, that mean the device is a
    // gateway and we should show different UI.
    const isGateway = useMemo(() => {
        if (gatewayCatalog && selectedDevice) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            }) !== undefined;
        }

        if (props.gatewaysOnly) {
            return true;
        }

        return false;
    }, [gatewayCatalog, selectedDevice, props.gatewaysOnly]);


    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [inputErrors, setInputErrors] = useState<InputErrors>({
        deviceClass: null,
    });

    // Cache user input value so we can use it in error messages
    const [userInputs, cacheUserInput] = useState<BaseDeviceInfoUpdatableInputs | undefined>();


    /**
     * Because this container can be used to display ALL type of devices or only Gateways therefore the translations will depend on whether we are
     * displaying all or gatewaysOnly. If we are displaying all type of devices then all the text/labels/messages will use the term "device"
     * for devices. If we are showing only gateways then instead of using the term "device", we will show a more specific term, "gateway".
     * This map DOES NOT include all the text we use on this page, only the text that has the word "device" in it.
     */
    const labels = useMemo((): {
        readonly panelTitle: string;
        readonly panelIcon: string;
        readonly device: string;
        readonly deviceClass: string;
        readonly deviceAddedSuccessfullyKey: string;
        readonly addDeviceConfirmDialogTitleKey: string;
        readonly addDeviceConfirmDialogMessageKey: string;
        readonly deviceNamePlaceholder: string;
        readonly inputErrorDeviceClassEmpty: string;
        readonly inputErrorDeviceClassInvalid: string;
    } => {
        if (isGateway) {
            return {
                panelTitle                      : capitalizeAll(trans(props.deviceId ? 'label.gateway_info' : 'label.add_gateway')),
                panelIcon                       : trans('icons.gateway'),
                device                          : capitalizeFirst(trans('terms.gateway')),
                deviceClass                     : capitalizeFirst(trans('terms.gateway_class')),
                deviceAddedSuccessfullyKey      : 'notif.gateway_added_successfully',
                addDeviceConfirmDialogTitleKey  : 'dialog.confirm_add_home_gateway.title',
                addDeviceConfirmDialogMessageKey: 'dialog.confirm_add_home_gateway.message',
                deviceNamePlaceholder           : 'input.gateway_name.placeholder',
                inputErrorDeviceClassEmpty      : 'error.input.gateway_class.empty',
                inputErrorDeviceClassInvalid    : 'error.input.gateway_class.invalid',
            };
        }

        return {
            panelTitle                      : capitalizeAll(trans(props.deviceId ? 'label.device_info' : 'label.add_device')),
            panelIcon                       : trans('icons.device'),
            device                          : capitalizeFirst(trans('terms.device')),
            deviceClass                     : capitalizeFirst(trans('terms.device_class')),
            deviceAddedSuccessfullyKey      : 'notif.device_added_successfully',
            addDeviceConfirmDialogTitleKey  : 'dialog.confirm_add_home_device.title',
            addDeviceConfirmDialogMessageKey: 'dialog.confirm_add_home_device.message',
            deviceNamePlaceholder           : 'input.device_name.placeholder',
            inputErrorDeviceClassEmpty      : 'error.input.device_class.empty',
            inputErrorDeviceClassInvalid    : 'error.input.device_class.invalid',
        };
    }, [isGateway, trans, props.deviceId]);



    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        if (props.deviceId && !selectedDevice) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => { /* ignore error */ });
        }
    }, [dataDispatch, selectedDevice, props.projectId, props.deviceId]);



    // Create a list of deviceClass/gateway options use for device class dropdown in the DeviceInfoComp
    // If props.gatewaysOnly, we will hide all the device classes that are not gateway
    const deviceClassOptions = useMemo((): readonly SelectInputOption<string>[] => {
        if (props.gatewaysOnly && gatewayCatalog) {
            // use the gateway catalog to build the list of gatewayClass options
            return gatewayCatalog.map((gateway: GatewayDefinition): SelectInputOption<string> => {
                return {
                    label: gateway.name || gateway.deviceClass,
                    value: gateway.deviceClass,
                };
            });
        }

        if (!props.gatewaysOnly && deviceClasses) {
            // use the deviceClasses to build the list of gatewayClass options
            return deviceClasses.map((deviceClass: DeviceClass) => {
                return {
                    label: deviceClass.id,
                    value: deviceClass.id,
                };
            });
        }

        return [];
    }, [deviceClasses, gatewayCatalog, props.gatewaysOnly]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.deviceId is undefined
     */
    useEffect(() => {
        if (props.deviceId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.deviceId, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            deviceClass: null,
        });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onDeviceBasicInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);



    /**
     * On Add On-demand provision device. We will CREATE a device first and then add that device to the home
     */
    const addDeviceToHome = useCallback(async (homeId: number, deviceClass: string | undefined, deviceTag: string | undefined) => {
        const newInputErrors: InputErrors = {
            deviceClass: null,
        };

        // validate deviceClass
        if (!deviceClass) {
            newInputErrors.deviceClass = labels.inputErrorDeviceClassEmpty;
        } else if (props.gatewaysOnly && (!deviceClasses || !deviceClasses.find((deviceClassDef: DeviceClass) => {
            return deviceClassDef.id === deviceClass;
        }))) {
            // If props.gatewaysOnly then need to check if deviceClass exist in the deviceClasses list
            newInputErrors.deviceClass = labels.inputErrorDeviceClassInvalid;
        }
        setInputErrors(newInputErrors);


        if (!newInputErrors.deviceClass && deviceClass) {
            uiDispatch(showDialog({
                type          : DialogType.CONFIRM,
                title         : labels.addDeviceConfirmDialogTitleKey,
                message       : labels.addDeviceConfirmDialogMessageKey,
                messageData   : { device_name: deviceClass },
                positiveButton: 'label.yes',
                negativeButton: 'label.cancel',
                onClose       : async (response?: DialogResponse): Promise<void> => {
                    if (response && response.code === DialogResponseCode.POSITIVE) {
                        try {
                            const provisionData = await AppAPI.getInstance().startDeviceProvisioning(homeId, deviceClass, deviceTag);
                            await AppAPI.getInstance().addOndemandProvisionedHomeDevice(homeId, deviceClass, provisionData.token);
                            uiDispatch(showSuccessNotification(labels.deviceAddedSuccessfullyKey));
                            closeEditing();
                            if (props.newDeviceSettings) {
                                props.newDeviceSettings.onNewDeviceAdded();
                            }
                        } catch (error) {
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                        }
                    }
                },
            }));
        }
    }, [props.gatewaysOnly, props.newDeviceSettings, deviceClasses, labels.inputErrorDeviceClassEmpty, labels.inputErrorDeviceClassInvalid,
        labels.addDeviceConfirmDialogTitleKey, labels.addDeviceConfirmDialogMessageKey, labels.deviceAddedSuccessfullyKey, uiDispatch, closeEditing]);



    /**
     * Handler for when the user click Save input from the user form
     */
    const onDeviceBasicInfoFormSubmit = useCallback(async (inputs: BaseDeviceInfoUpdatableInputs) => {
        if (!project) {
            return;
        }

        const { deviceClass, name, tag } = inputs;
        cacheUserInput(inputs);


        if (!props.deviceId && props.homeId) {
            // This this container is used for adding new device to a home, call onAddDevice
            addDeviceToHome(props.homeId, deviceClass, tag);
        } else if (props.deviceId && selectedDevice) {
            // If this is used for editing an existing device

            // Name and Tag are optional AND can be empty string so no need to validate
            if ((name !== undefined && name !== selectedDevice.name) || (tag !== undefined && tag !== selectedDevice.tag)) {
                try {
                    // dispatch an action to update the device name and tag
                    await dataDispatch(updateProjectDevice(props.projectId, selectedDevice.id, { name, tag }));

                    // If there is a homeId, refetch the home device data from MAZE as well
                    if (props.homeId) {
                        await appDataDispatch(fetchHomeDevice(props.homeId, selectedDevice.id));
                    }

                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: labels.device }));

                    closeEditing();
                } catch (error) {
                    /* ignore error */
                }
            } else {
                // newName is the same so no need to make api call
                closeEditing();
            }
        }
    }, [project, props.deviceId, props.homeId, props.projectId, selectedDevice, addDeviceToHome, dataDispatch, appDataDispatch, uiDispatch,
        labels.device, closeEditing]);



    /**
     * On Cancel edit, call closeEditing to clear input errors and edit mode AND if we are trying to create new device, also dispatch an
     * event to the container to let the container know the create device flow is canceled.
     */
    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newDeviceSettings !== undefined) {
            props.newDeviceSettings.onNewDeviceCanceled();
        }
    }, [props.newDeviceSettings, closeEditing]);


    const inputSettings = useMemo((): DeviceBasicInfoProps['fields'] => {
        const defaultSettings = {
        };

        if (project) {
            if (props.deviceId && selectedDevice) {
                // This if for viewing/editing existing device, show all props
                return {
                    ...defaultSettings,
                    id: {
                        label       : capitalizeAll(trans('label.id')),
                        displayValue: selectedDevice.id.toString(),
                        value       : selectedDevice.id,
                    },
                    deviceClass: {
                        label       : labels.deviceClass,
                        displayValue: selectedDevice.deviceClass,
                        value       : selectedDevice.deviceClass,
                    },
                    name: {
                        label       : capitalizeAll(trans('label.name')),
                        displayValue: selectedDevice.name ? selectedDevice.name : capitalizeAll(trans('label.none')),
                        placeholder : trans(labels.deviceNamePlaceholder),
                        value       : selectedDevice.name,
                        editable    : true,
                    },
                    tag: {
                        label       : capitalizeAll(trans('label.tag')),
                        displayValue: selectedDevice.tag ? selectedDevice.tag : capitalizeAll(trans('label.none')),
                        placeholder : trans('input.device_tag.placeholder'),
                        value       : selectedDevice.tag,
                        editable    : true,
                    },
                    firmware: {
                        label       : capitalizeAll(trans('label.firmware')),
                        displayValue: selectedDevice.channel ? selectedDevice.channel : capitalizeAll(trans('label.none')),
                        value       : selectedDevice.channel,
                    },
                    bundleInstalled: {
                        label       : capitalizeAll(trans('label.installed_bundle')),
                        displayValue: selectedDevice.bundleInstalled ? selectedDevice.bundleInstalled : capitalizeAll(trans('label.none')),
                        value       : selectedDevice.bundleInstalled,
                        hidden      : selectedDevice.channel === undefined || selectedDevice.bundleInstalled === undefined,
                    },
                    bundleInstallTime: {
                        label       : capitalizeAll(trans('label.bundle_install_time')),
                        displayValue: getDateTimeAndElapsedTime(selectedDevice.bundleInstallTime, trans),
                        value       : selectedDevice.bundleInstallTime,
                        hidden      : selectedDevice.channel === undefined || selectedDevice.bundleInstallTime === undefined,
                    },
                    claimTime: {
                        label       : capitalizeAll(trans('label.claim_date')),
                        displayValue: getDateTimeAndElapsedTime(selectedDevice.claimTime, trans),
                        value       : selectedDevice.claimTime,
                    },
                };
            }

            if (!props.deviceId) {
                // Creating device so only need some of the input settings
                // If project's device type is ON-DEMAND Provision, the user has to pick a deviceClass and provide an optional tag
                if (project.onDemandProvisioning) {
                    return {
                        ...defaultSettings,
                        deviceClass: {
                            label       : labels.deviceClass,
                            displayValue: '',
                            editable    : true,
                            options     : deviceClassOptions,
                            error       : inputErrors.deviceClass ? capitalizeFirst(trans(inputErrors.deviceClass, {
                                device_class: userInputs?.deviceClass,
                            })) : '',
                        },
                        tag: {
                            label       : capitalizeAll(trans('label.tag')),
                            displayValue: '',
                            placeholder : trans('input.device_tag.placeholder'),
                            editable    : true,
                        },
                    };
                }

                // If project's device provision type is PRE-PROVISION, the user has to select one of the existing UNCLAIMED devices
                return {
                    ...defaultSettings,
                    id: {
                        label       : capitalizeAll(trans('label.id')),
                        displayValue: '',
                        value       : 0,
                    },
                };

            }
        }

        return defaultSettings;
    }, [project, props.deviceId, selectedDevice, trans, labels.deviceClass, labels.deviceNamePlaceholder, deviceClassOptions, inputErrors.deviceClass,
        userInputs]);


    return (
        <>
            {project && (!props.deviceId || selectedDevice) && (
                <DeviceBasicInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={labels.panelTitle}
                    icon={labels.panelIcon}
                    fields={inputSettings}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onDeviceBasicInfoFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onDeviceBasicInfoFormInputChange}
                />
            )}

            {(!project || (props.deviceId && !selectedDevice)) && (
                <CircularProgress />
            )}
        </>
    );
};
